import React, {
  useEffect,
  useState,
  useCallback,
  memo,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { baseurl } from "../config/url";


/* ----------------- CONFIG & HELPERS ----------------- */
const getApiBase = () => {
  if (typeof window === "undefined") return baseurl || "";
  const isLocal = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  return isLocal ? "http://localhost:5000" : baseurl || "";
};

const API_BASE = getApiBase();

const joinUrl = (base, path) => {
  if (!path) return base || "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = String(path).startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
};

const toSrc = (img) => {
  if (!img) return null;
  if (typeof img === "string") return joinUrl(API_BASE, img);
  if (img?.url) return joinUrl(API_BASE, img.url);
  return null;
};

const formatINR = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const normalizeListResponse = (data) => {
  if (data?.products) return { items: data.products, success: true };
  if (Array.isArray(data)) return { items: data, success: true };
  return { items: [], success: false };
};

const CACHE_KEY = "allProducts_v1";
const CACHE_TTL_MS = 5 * 60 * 1000;
const PAGE_SIZE = 10;

/* ----------------- PRODUCT CARD ----------------- */
const ProductCard = memo(({ product, onClick }) => {
  const img = toSrc(product?.image);
  const name = product?.name || "Product";

  const price =
    product?.price !== undefined && Number(product.price) > 0
      ? Number(product.price)
      : null;

  return (
    <article
      onClick={onClick}
      className="group cursor-pointer flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-3 sm:p-4"
    >
      {/* Badge */}
      <div className="flex justify-between text-[11px] mb-2">
        <span className="px-2 py-0.5 rounded-full bg-[#E3EEFF] border border-slate-200 text-slate-700">
          In Demand
        </span>
        <span className="text-slate-400 uppercase tracking-wide">BuildPro</span>
      </div>

      {/* Image */}
      <div className="relative w-full">
        <div className="aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden">
          {img ? (
            <img
              src={img}
              alt={name}
              className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-[1.04]"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              No Image
            </div>
          )}
        </div>

        {price && (
          <span className="absolute bottom-2 left-2 bg-white/90 px-2.5 py-1 text-xs font-semibold rounded-full border border-slate-200 shadow">
            {formatINR(price)}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="mt-3">
        <h3 className="text-sm font-semibold text-slate-900 line-clamp-2">
          {name}
        </h3>
        <p className="text-[11px] mt-1 text-slate-500">
          Quality checked • Quick enquiry • Best value
        </p>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation(); // so card onClick doesn't fire twice
            onClick();
          }}
          className="mt-3 w-full px-4 py-2 rounded-full text-xs font-medium shadow text-slate-700 bg-[#DDEBFF] hover:bg-[#C7DFFF] transition"
        >
          View Details
        </button>
      </div>
    </article>
  );
});

/* ----------------- MAIN COMPONENT ----------------- */
export default function AllProducts() {
  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const navigate = useNavigate();
 

  const goToDetails = useCallback(
    (id) => id && navigate(`/product/details/${id}`),
    [navigate]
  );

  /* ----- Load From Cache ----- */
  const loadCached = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}");
      if (cached.timestamp && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        setProducts(cached.items || []);
        setVisibleCount(Math.min(PAGE_SIZE, (cached.items || []).length));
        setLoading(false);
        return true;
      }
    } catch {
      // ignore cache errors
    }
    return false;
  };

  /* ----- Fetch Products ----- */
  useEffect(() => {
    if (loadCached()) return;

    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setErr("");

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const endpoints = [
        "/api/product/all",
        "/api/products",
        "/api/product",
        "/api/products/all",
      ];

      let found = null;

      for (const ep of endpoints) {
        if (found) break;
        try {
          const url = joinUrl(API_BASE, ep);
          const res = await axios.get(url, {
            params: { page: 1, limit: 200 },
            headers,
            signal: controller.signal,
          });
          if (res.status >= 200 && res.status < 300) {
            found = res.data;
            break;
          }
        } catch {
          // try next endpoint
        }
      }

      if (!found) {
        setErr("Unable to load products.");
        setLoading(false);
        return;
      }

      const norm = normalizeListResponse(found);
      setProducts(norm.items);
      setVisibleCount(Math.min(PAGE_SIZE, norm.items.length));
      setLoading(false);

      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ items: norm.items, timestamp: Date.now() })
        );
      } catch {
        // ignore cache write errors
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  const currentItems = products.slice(0, visibleCount);
  const remaining = Math.max(products.length - visibleCount, 0);

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, products.length));
  };

  /* ----------------- SEO ----------------- */
  const seoTitle = "Explore Our Products | BuildPro-OGCS";
  const seoDesc =
    "Browse high-quality construction materials including AAC blocks, TMT steel, shuttering, chemicals, lighting, and more.";

  /* ----------------- UI ----------------- */
  return (
    <div className="min-h-screen bg-[#EFF6FF]">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* HEADER (matching Buyerschoice style) */}
      <header className="pt-10 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#DDEBFF] bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C7DFFF]" />
            BuildPro-OGCS • Product Gallery
          </p>

          <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900">
            <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
              Current Products & Live Listings
            </span>
          </h1>

          <p className="mt-2 text-sm text-slate-600">
            Browse live products and click any card to view full specifications, pricing
            and quick enquiry options for your site needs.
          </p>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
        {err && (
          <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {err}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-4 animate-pulse"
              >
                <div className="aspect-[4/3] w-full bg-slate-100 rounded-xl mb-3" />
                <div className="h-3.5 bg-slate-100 rounded w-4/5 mb-2" />
                <div className="h-3.5 bg-slate-100 rounded w-2/5" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 mt-1 text-sm">No products found.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
              {currentItems.map((p, i) => (
                <ProductCard
                  key={p._id || i}
                  product={p}
                  onClick={() => goToDetails(p._id)}
                />
              ))}
            </div>

            {/* SHOW REMAINING BUTTON */}
            {remaining > 0 && (
              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={loadMore}
                  className="px-4 py-2 text-xs font-medium bg-white border border-slate-300 rounded-full shadow-sm hover:bg-[#E3EEFF] transition-colors duration-150"
                >
                  Show remaining {remaining} item{remaining > 1 ? "s" : ""}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
